/**
 * Main Layout Component
 * Contains routing and navigation structure
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

import NavDrawer from './elements/NavDrawer';
import DashboardPage from './pages/DashboardPage';
import ProjectListPage from './pages/ProjectListPage';
import ProjectPage from './pages/ProjectPage';
import ProjectSetupWizard from './pages/ProjectSetupWizard';
import AssetPage from './pages/AssetPage';
import MyAccountPage from './pages/MyAccountPage';
import SearchResultsPage from './pages/SearchResultsPage';
import UsersPage from './pages/UsersPage';

// Admin pages
import CustomersPage from './pages/admin/CustomersPage';
import AssetracUsersPage from './pages/admin/AssetracUsersPage';
import ReassignmentPage from './pages/admin/ReassignmentPage';
import GlobalAssetTypesPage from './pages/admin/GlobalAssetTypesPage';
import AreaTypesPage from './pages/admin/AreaTypesPage';
import ReferenceDataPage from './pages/admin/ReferenceDataPage';
import MovementReportsPage from './pages/MovementReportsPage';
import AlertsPage from './pages/AlertsPage';

// Manual count pages (Iteration 12)
import HomePage from './pages/HomePage';
import ManualMapPage from './pages/ManualMapPage';
import EntryLogPage from './pages/EntryLogPage';
import TruckerLoginPage from './pages/TruckerLoginPage';
import TruckerCountPage from './pages/TruckerCountPage';
import ManualAssetsPage from './pages/ManualAssetsPage';
import ManualAssetCountsPage from './pages/ManualAssetCountsPage';

import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCustomers } from '../store/slices/customerSlice';
import { fetchAssetTypes, fetchAssetSubTypes, fetchAssetOwners } from '../store/slices/assetSlice';
import { memberOf } from '../store/slices/authSlice';
import { loadIcons } from '../store/slices/uiSlice';
import { useCapability } from '../hooks/useCapability';

/**
 * Route wrapper for admin-only pages
 */
const AdminRoute = ({ children }) => {
  const dispatch = useAppDispatch();
  const { userGroups } = useAppSelector((state) => state.auth);
  const [isAdmin, setIsAdmin] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      const result = await dispatch(memberOf('admins'));
      setIsAdmin(result);
      setChecking(false);
    };
    checkAdmin();
  }, [dispatch, userGroups]);

  if (checking) {
    return null; // Or a loading spinner
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Route wrapper for manual-count pages (Iteration 12)
 * Redirects to / if customer doesn't have manual capability
 *
 * R-52 v3 (ASS-61): the three-state problem — allowed / not allowed / NOT YET
 * KNOWN — is closed at the store, not here. `customerSlice`'s initialState
 * hydrates `activeCustomer` synchronously from localStorage on module load, so
 * ManualRoute has a real customer object at render #1 on hard-load / deep-link.
 * That collapses the third state into either 'known-from-cache' (allowed or not
 * based on the cached trackingMode) or 'no cache' (redirect — correct for a
 * user with no prior session).
 *
 * v1 treated NOT-YET-KNOWN as KNOWN-NEGATIVE via useCapability's || 'beacon'
 * default. v2 treated FETCH-NOT-STARTED as NO-CUSTOMER because customerLoading
 * alone can't distinguish 'about to fetch' from 'never will'. v3 removes the
 * timing question from the route entirely by making activeCustomer synchronous
 * at boot; the fetch just refreshes what's already there.
 *
 * Guards unchanged: D-1's || 'beacon' default in useCapability stays; the
 * capability gate itself is unchanged.
 */
const ManualRoute = ({ children }) => {
  const capability = useCapability();
  if (!capability.canAccessManualSurfaces) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const Layout = () => {
  const dispatch = useAppDispatch();
  const { activeCustomer } = useAppSelector((state) => state.customer);

  // Load initial data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      await dispatch(fetchCustomers());
      await dispatch(fetchAssetTypes());
      await dispatch(fetchAssetSubTypes());
      await dispatch(fetchAssetOwners());
      dispatch(loadIcons());
    };
    
    loadInitialData();
  }, [dispatch]);

  return (
    <Router>
      <NavDrawer />
      <Routes>
        {/* Main routes (all users) */}
        <Route path="/" element={<HomePage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/projects" element={<ProjectListPage />} />
        <Route path="/projects/new" element={<ProjectSetupWizard />} />
        <Route path="/projects/:id" element={<ProjectPage />} />
        <Route path="/projects/:id/edit" element={<ProjectPage />} />
        <Route path="/asset/:id" element={<AssetPage />} />
        <Route path="/account" element={<MyAccountPage />} />
        <Route path="/search" element={<SearchResultsPage />} />
        <Route path="/users" element={<UsersPage />} />
        {/* AST-114: NOT wrapped in AdminRoute -- that's staff-only and would
            lock out Customer Admins. The page checks access itself and shows
            an access-denied state without fetching (NAV-12); the API
            re-checks every request. */}
        <Route path="/admin/manual-assets" element={<ManualAssetsPage />} />
        {/* AST-126: every user who sees Projects (MC-3). The data calls are
            the per-project reads any user of the customer may make. */}
        <Route path="/assets/manual-counts" element={<ManualAssetCountsPage />} />
        <Route path="/reports" element={<MovementReportsPage />} />
        <Route path="/alerts" element={<AlertsPage />} />

        {/* Manual count routes (Iteration 12) — gated by capability */}
        <Route
          path="/manual/map"
          element={
            <ManualRoute>
              <ManualMapPage />
            </ManualRoute>
          }
        />
        <Route
          path="/manual/scoreboard"
          element={
            <ManualRoute>
              <ManualMapPage />
            </ManualRoute>
          }
        />
        <Route
          path="/manual/log"
          element={
            <ManualRoute>
              <EntryLogPage />
            </ManualRoute>
          }
        />
        <Route
          path="/manual/entry"
          element={
            <ManualRoute>
              <ManualMapPage />
            </ManualRoute>
          }
        />

        {/* Trucker routes (Iteration 12) */}
        <Route path="/trucker/login" element={<TruckerLoginPage />} />
        <Route
          path="/trucker/count"
          element={
            <ManualRoute>
              <TruckerCountPage />
            </ManualRoute>
          }
        />
        
        {/* Admin routes */}
        <Route 
          path="/admin/users" 
          element={
            <AdminRoute>
              <AssetracUsersPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/customers" 
          element={
            <AdminRoute>
              <CustomersPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/reassignment" 
          element={
            <AdminRoute>
              <ReassignmentPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/asset-types" 
          element={
            <AdminRoute>
              <GlobalAssetTypesPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/area-types" 
          element={
            <AdminRoute>
              <AreaTypesPage />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/reference-data" 
          element={
            <AdminRoute>
              <ReferenceDataPage />
            </AdminRoute>
          } 
        />
        
        {/* Fallback route */}
        <Route path="*" element={<HomePage />} />
      </Routes>
    </Router>
  );
};

export default Layout;
